import { useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { StatusBadge } from '@/components/StatusBadge';
import { Phone, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { usePermissions } from '@/hooks/usePermissions';
import { api, getApiErrorMessage, occurrenceEndpoints, occurrenceStatusEndpoints } from '@/lib/api';
import { PERMISSIONS } from '@/lib/permissions';

const INCIDENT_ACTION_PERMISSIONS = {
  create: PERMISSIONS.INCIDENT_CREATE,
  read: PERMISSIONS.INCIDENT_READ,
  update: PERMISSIONS.INCIDENT_UPDATE,
  delete: PERMISSIONS.INCIDENT_DELETE,
};

const collectionPayload = (payload, key) => {
  const source = payload?.data ?? payload;
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.data)) return source.data;
  if (Array.isArray(source?.[key])) return source[key];
  return [];
};

const unwrapAttributes = (source = {}) => {
  const attributes = source?.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
    ? source.attributes
    : {};
  return {...source, ...attributes};
};

const normalizeStatusValue = (status) => {
  const source = status && typeof status === 'object' ? unwrapAttributes(status) : {};
  return String(source.code ?? status ?? '').toLowerCase();
};

const normalizeOccurrenceStatus = (occurrence) => {
  const status = occurrence.occurrence_status ?? occurrence.status_record ?? occurrence.status;
  const code = normalizeStatusValue(status);
  return code || 'pendente';
};

const splitDateTime = (value) => {
  if (!value) return {date: '', time: ''};
  const normalized = String(value).replace('T', ' ');
  const [date = '', time = ''] = normalized.split(' ');
  return {date, time: time.slice(0, 5)};
};

const normalizeOccurrence = (source = {}, index = 0) => {
  const occurrence = unwrapAttributes(source);
  const resource = occurrence.resource ?? occurrence.vehicle ?? occurrence.object ?? {};
  const tag = occurrence.tag ?? resource.tag ?? {};
  const type = occurrence.occurrence_type ?? occurrence.type ?? {};
  const reportedAt = splitDateTime(occurrence.reported_at ?? occurrence.occurred_at ?? occurrence.created_at ?? occurrence.date);

  return {
    id: occurrence.code ?? occurrence.reference ?? occurrence.id ?? `OCR-${index + 1}`,
    tag: tag.code ?? tag.tag_code ?? tag.id ?? occurrence.tag_code ?? occurrence.tag_id ?? '-',
    type: type.name ?? type.title ?? occurrence.type_name ?? occurrence.occurrence_type_name ?? occurrence.title ?? 'Ocorrência',
    vehicle: resource.reference ?? resource.code ?? resource.name ?? occurrence.resource_reference ?? occurrence.resource_code ?? '-',
    status: normalizeOccurrenceStatus(occurrence),
    date: occurrence.date ?? reportedAt.date,
    time: occurrence.time ?? reportedAt.time,
    origin: occurrence.origin ?? occurrence.source ?? occurrence.channel ?? 'web',
    calledPhone: Boolean(occurrence.called_phone ?? occurrence.phone_called ?? occurrence.requires_call ?? occurrence.call_required ?? false),
    location: occurrence.location ?? occurrence.address ?? occurrence.location_name ?? occurrence.place ?? '-',
  };
};

const normalizeOccurrences = (payload) => {
  const occurrences = collectionPayload(payload, 'occurrences');
  const items = occurrences.length ? occurrences : collectionPayload(payload, 'incidents');
  return items.map(normalizeOccurrence);
};

const normalizeOccurrenceStatusOptions = (payload) => {
  const statuses = collectionPayload(payload, 'occurrence_statuses');
  const items = statuses.length ? statuses : collectionPayload(payload, 'statuses');
  const seen = new Set();

  return items.reduce((options, source) => {
    const status = unwrapAttributes(source);
    const code = String(status.code ?? '').toLowerCase();
    const name = String(status.name ?? '').trim();

    if (!code || !name || seen.has(code)) return options;

    seen.add(code);
    options.push({code, name});
    return options;
  }, []);
};

export const IncidentsPage = () => {
  const { can } = usePermissions();
  const [incidents, setIncidents] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const incidentPermissions = {
    create: can(INCIDENT_ACTION_PERMISSIONS.create),
    read: can(INCIDENT_ACTION_PERMISSIONS.read),
    update: can(INCIDENT_ACTION_PERMISSIONS.update),
    delete: can(INCIDENT_ACTION_PERMISSIONS.delete),
  };
  const canReadIncidents = incidentPermissions.read;

  useEffect(() => {
    let cancelled = false;

    const loadOccurrences = async () => {
      if (!canReadIncidents) {
        setIncidents([]);
        setStatusOptions([]);
        setError('Nao tem permissão para consultar ocorrências.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const [occurrencesResponse, statusesResponse] = await Promise.all([
          api.get(occurrenceEndpoints.list),
          api.get(occurrenceStatusEndpoints.list),
        ]);

        if (!cancelled) {
          setIncidents(normalizeOccurrences(occurrencesResponse.data));
          setStatusOptions(normalizeOccurrenceStatusOptions(statusesResponse.data));
        }
      } catch (err) {
        if (!cancelled) {
          setIncidents([]);
          setStatusOptions([]);
          setError(getApiErrorMessage(err, 'Nao foi possível carregar as ocorrências.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadOccurrences();

    return () => {
      cancelled = true;
    };
  }, [canReadIncidents]);

  const typeOptions = useMemo(
    () => Array.from(new Set(incidents.map(incident => incident.type).filter(Boolean))),
    [incidents]
  );

  const statusLabelByCode = useMemo(
    () => new Map(statusOptions.map(status => [status.code, status.name])),
    [statusOptions]
  );

  const filteredIncidents = incidents.filter(incident => {
    const matchesStatus = statusFilter === 'all' || incident.status === statusFilter;
    const matchesType = typeFilter === 'all' || incident.type === typeFilter;
    return matchesStatus && matchesType;
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Gestao de Ocorrencias</h1>
            <p className="text-slate-600 font-primary mt-1">Monitorizar e gerir ocorrencias da frota</p>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Select value={statusFilter} onValueChange={setStatusFilter} disabled={!canReadIncidents || loading}>
                  <SelectTrigger data-testid="incident-status-filter" className="w-48">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos Estados</SelectItem>
                    {statusOptions.map(status => (
                      <SelectItem key={status.code} value={status.code}>{status.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={setTypeFilter} disabled={!canReadIncidents || loading}>
                  <SelectTrigger data-testid="incident-type-filter" className="w-56">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos Tipos</SelectItem>
                    {typeOptions.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-600">Total:</span>
                <span className="font-secondary font-bold text-slate-900">{filteredIncidents.length}</span>
              </div>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Tag / Veiculo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Localizacao</TableHead>
                <TableHead>Data / Hora</TableHead>
                <TableHead>Origem</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && filteredIncidents.map((incident) => (
                <TableRow key={incident.id} data-testid={`incident-row-${incident.id}`} className="cursor-pointer hover:bg-slate-50">
                  <TableCell className="font-secondary font-bold text-slate-900">{incident.id}</TableCell>
                  <TableCell>
                    <div>
                      <p className="text-slate-900 font-medium">{incident.tag}</p>
                      <p className="text-slate-600 text-xs">{incident.vehicle}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{incident.type}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{incident.location}</TableCell>
                  <TableCell>
                    <div>
                      <p className="text-slate-900 text-sm">{incident.date}</p>
                      <p className="text-slate-600 text-xs">{incident.time}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {incident.origin === 'app' ? (
                        <><Smartphone size={12} className="mr-1" /> App</>
                      ) : (
                        <><Phone size={12} className="mr-1" /> Web</>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {incident.calledPhone ? (
                      <div className="flex items-center gap-1 text-emerald-600">
                        <Phone size={14} />
                        <span className="text-xs">Chamada</span>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">Notificacao</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={incident.status} type="incident" label={statusLabelByCode.get(incident.status)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">A carregar ocorrencias...</div>
          ) : filteredIncidents.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">Nenhuma ocorrencia encontrada.</div>
          )}

          <div className="p-4 border-t border-slate-200 flex items-center justify-between text-sm text-slate-600">
            <span>A mostrar {filteredIncidents.length} de {incidents.length} ocorrencias</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">Anterior</Button>
              <Button variant="outline" size="sm">Proximo</Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
